import React from 'react';
import { useStore } from '../store/useStore';
import ProjectHome     from '../pages/ProjectHome';
import ProjectDrawings from '../pages/ProjectDrawings';
import ProjectFiles    from '../pages/ProjectFiles';
import ProjectConcrete from '../pages/ProjectConcrete';
import ProjectForms    from '../pages/ProjectForms';
import ProjectPhotos   from '../pages/ProjectPhotos';
import ProjectMembers  from '../pages/ProjectMembers';
import ProjectSettings from '../pages/ProjectSettings';
import styles from './ProjectLayout.module.css';

/* ── Icons (must be declared before NAV) ── */
const HomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M3 9.5L10 3l7 6.5V17a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"
      stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    <path d="M7.5 18v-6h5v6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const DrawingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="2" width="11" height="14" rx="1" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M6 6h5M6 9h5M6 12h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <rect x="8" y="7" width="9" height="11" rx="1" fill="var(--surface)" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M10 11h5M10 14h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const FilesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M5 3h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"
      stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    <path d="M12 3v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const FormsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="3" width="14" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <circle cx="5.5" cy="7" r="1" fill="currentColor"/>
    <circle cx="5.5" cy="10" r="1" fill="currentColor"/>
    <circle cx="5.5" cy="13" r="1" fill="currentColor"/>
  </svg>
);
const PhotosIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="4" width="16" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="7" cy="9" r="2" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M2 14l4-4 3 3 3-4 5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 4V2.5M10 4V2.5M14 4V2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const MembersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M2 17c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M14 8a3 3 0 000-6M18 17c0-2.761-1.79-5.1-4.274-5.834"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
// Concrete mixer truck — stylised drum + chute + wheels.
const ConcreteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    {/* Drum (canted ellipse) */}
    <ellipse cx="11" cy="9" rx="5.5" ry="4" stroke="currentColor" strokeWidth="1.3"/>
    {/* Drum spiral hint — two arcs evoking the auger */}
    <path d="M7 7c2 1.5 4 1.5 8 .5M6.5 10c2 1.5 4 1.5 8 0"
      stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
    {/* Cab */}
    <path d="M2 12V8h3v4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    {/* Chassis bar */}
    <path d="M2 13h15" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    {/* Wheels */}
    <circle cx="5" cy="15" r="1.6" stroke="currentColor" strokeWidth="1.3"/>
    <circle cx="14" cy="15" r="1.6" stroke="currentColor" strokeWidth="1.3"/>
  </svg>
);

/* ── Nav config (icons are now defined above) ── */
const NAV = [
  { id: 'home',     label: 'Home',     Icon: HomeIcon     },
  { id: 'drawings', label: 'Drawings', Icon: DrawingsIcon },
  // Concrete sits below Drawings and above Files per the spec.
  { id: 'concrete', label: 'Concrete', Icon: ConcreteIcon },
  { id: 'files',    label: 'Files',    Icon: FilesIcon    },
  { id: 'forms',    label: 'Forms',    Icon: FormsIcon    },
  { id: 'photos',   label: 'Photos',   Icon: PhotosIcon   },
  { id: 'members',  label: 'Members',  Icon: MembersIcon  },
  { id: 'settings', label: 'Settings', Icon: SettingsIcon },
];

export default function ProjectLayout() {
  const {
    projectPage, setProjectPage, setDrawingsViewMode,
    navCollapsed, toggleNavCollapsed,
    project,
  } = useStore();

  // Pages rendered inside the component so they re-render correctly
  const page = {
    home:     <ProjectHome />,
    drawings: <ProjectDrawings />,
    concrete: <ProjectConcrete />,
    files:    <ProjectFiles />,
    forms:    <ProjectForms />,
    photos:   <ProjectPhotos />,
    members:  <ProjectMembers />,
    settings: <ProjectSettings />,
  }[projectPage] ?? <ProjectHome />;

  return (
    <div className={styles.layout}>
      <nav className={`${styles.nav} ${navCollapsed ? styles.navCollapsed : ''}`}>
        <button
          className={styles.collapseBtn}
          onClick={toggleNavCollapsed}
          title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <CollapseIcon collapsed={navCollapsed} />
          {!navCollapsed && <span className={styles.collapseLabel}>Collapse</span>}
        </button>

        {NAV.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={`${styles.navItem} ${projectPage === id ? styles.navActive : ''}`}
            onClick={() => {
              setProjectPage(id);
              // Clicking the Drawings nav item always lands on the list —
              // this is the way to return from the plan viewer back to the
              // sheet table, replacing the old in-viewer DRAWINGS button.
              if (id === 'drawings') setDrawingsViewMode('list');
            }}
            // Title shows the label as a tooltip when collapsed, so iPad hover
            // or long-press still gives context even without the text label.
            title={navCollapsed ? label : undefined}
            aria-label={label}
          >
            <Icon />
            <span className={styles.navLabel}>{label}</span>
          </button>
        ))}
      </nav>

      {/*
        key={project?.id} forces React to unmount + remount the active
        page whenever the active project changes. That wipes every
        useState in the page component (filter inputs, edit-row state,
        modal flags, etc.) so nothing local leaks across projects —
        belt-and-suspenders for the store's project-scoped reset.
      */}
      <div className={styles.content} key={project?.id}>
        {page}
      </div>
    </div>
  );
}

/* Chevron points left (◀) when expanded to hint "click me to hide"; points
 * right (▶) when collapsed to hint "click me to reveal". */
const CollapseIcon = ({ collapsed }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    {collapsed ? (
      <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"/>
    ) : (
      <path d="M13 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"/>
    )}
  </svg>
);
